import {
  isAvailable, getAllRides,
  getMeta, setMeta, getLatestRideDate
} from './db.js';
import wahooAdapter from './adapters/wahoo.js';
import customUrlAdapter from './adapters/customUrl.js';
import fileImportAdapter from './adapters/fileImport.js';
import demoDataAdapter, { loadDemoRides } from './adapters/demoData.js';
import { initCharts, setRides, restoreSelection } from './charts.js';
import { initRides, setRides as setRideList, showRides } from './rides.js';
import { initRideModal } from './rideModal.js';
import { initSettings, openSettings } from './settings.js';
import {
  computeStats, fmtDistance, fmtSpeed, fmtInt, fmtDecimal1,
  fmtShortDate, fmtShortTime,
} from './dataProcessor.js';
import { getUserName } from './userName.js';
import { initTelemetry, initAnalytics, reportError } from './telemetry.js';
import { ingestLoad } from './dataLoader.js';

const syncAdapters = [wahooAdapter, customUrlAdapter, fileImportAdapter];

// One bundle, two entry pages: the dashboard and the Wahoo OAuth callback
// share `app.bundle.js` so the callback page gets `idb` resolved (a bare
// specifier the browser can't load directly from source). Dispatch by path
// to the right entry point; the adapter owns its own callback orchestration.
document.addEventListener('DOMContentLoaded', () => {
  // Fire-and-forget; errors during init must not block page render.
  _bootTelemetry();
  if (location.pathname.startsWith('/auth/wahoo/callback')) {
    return wahooAdapter.handleCallbackPage();
  }
  return initializeDashboard();
});

async function _bootTelemetry() {
  // The build runs before bundling, so version.js is always present in the
  // bundle. A dynamic import keeps a build-time miss from being a fatal app
  // error and lets esbuild keep the chunk small.
  let revision = '';
  try {
    const { version } = await import('../dist/version.js');
    revision = version?.shortSha || version?.ref || '';
  } catch { /* version.js absent — init with no revision */ }
  try { await initTelemetry({ revision }); } catch { /* swallow */ }
  try { initAnalytics(); } catch { /* swallow */ }
}

async function initializeDashboard() {
  const dbOk = await isAvailable();
  if (!dbOk) {
    document.getElementById('storage-banner')?.classList.remove('hidden');
  }

  if (dbOk) await migrateAdapterModel();

  showLoading();

  try {
    const [rides, lastSyncIso, activeAdapterId, hiddenSeasons] = await Promise.all([
      dbOk ? getAllRides() : [],
      dbOk ? getActiveSyncTimestamp() : null,
      dbOk ? getMeta('activeAdapter') : null,
      dbOk ? getMeta('hiddenSeasons') : null,
    ]);

    updateFooterSync(lastSyncIso);

    initCharts({
      onYearsChanged: (active, full) => {
        updateTagline(full);
        updateStats(active, full);
      },
      onTabChange: handleTabChange,
      // Persist the hidden-seasons set so the legend selection survives a
      // reload. Fire-and-forget — a failed write must not break interaction.
      onSelectionChange: (offIds) => {
        if (dbOk) setMeta('hiddenSeasons', offIds).catch(() => {});
      },
    });
    initRides();
    initRideModal({ onChange: refreshCharts });

    initSettings({
      onDataChange: handleDataChange,
      onUserNameChange: refreshMasthead,
    });
    bindCogButton();
    bindWelcomeDialog();
    bindDemoBanner();
    bindReauthBanner();

    restoreSelection(hiddenSeasons);
    setRides(rides);
    setRideList(rides);
    refreshMasthead();
    await updateDemoBanner(rides.length);
    await updateReauthBanner();

    // First visit (empty DB, no active source): load demo data straight into
    // the dashboard. The welcome dialog is only the can't-load fallback
    // (demo fetch failed, or IndexedDB unavailable so nothing can persist).
    if (rides.length === 0 && !activeAdapterId) {
      const ok = dbOk && await autoLoadDemo();
      if (ok) await refreshCharts();
      else openWelcome();
    }

    // The active adapter owns ongoing ingest; only sync-kind adapters re-fetch
    // on load (oneShot sources like demo just keep their stored rides).
    const adapter = activeAdapterId && syncAdapters.find(a => a.id === activeAdapterId);
    if (adapter?.kind === 'sync') _triggerAutosync(adapter);
  } catch (err) {
    console.error('Failed to initialize app:', err);
    reportError(err, { op: 'app-init' });
  } finally {
    hideLoading();
  }
}

export async function refreshCharts() {
  const rides = await getAllRides();
  setRides(rides);
  setRideList(rides);
  updateFooterSync(await getActiveSyncTimestamp());
  await updateDemoBanner(rides.length);
  await updateReauthBanner();
}

async function autoLoadDemo() {
  try {
    const batch = await loadDemoRides();
    await ingestLoad(demoDataAdapter, batch, { trigger: 'auto-demo' });
    return true;
  } catch (e) {
    reportError(e, { adapter: 'demo-data', op: 'auto-demo' });
    return false;
  }
}

// Marks demo rides as sample data while they're on screen. Hidden once a real
// source is active, the DB is empty, or the user dismissed it this session.
// The rideCount guard comes first so an empty/unavailable DB never hits getMeta.
async function updateDemoBanner(rideCount) {
  const banner = document.getElementById('demo-banner');
  if (!banner) return;
  const show = rideCount > 0
    && !sessionStorage.getItem('demoBannerDismissed')
    && (await getMeta('activeAdapter')) === 'demo-data';
  banner.classList.toggle('hidden', !show);
}

// wahooReauthRequired is set by the Wahoo adapter (see wahoo.js
// _clearExpiredSession) when a background autosync finds the refresh token
// dead — that's the common case, since the user isn't watching the Settings
// dialog when autosync runs. Cleared again on the next successful connect.
async function updateReauthBanner() {
  const banner = document.getElementById('reauth-banner');
  if (!banner) return;
  const show = !sessionStorage.getItem('reauthBannerDismissed')
    && !!(await getMeta('wahooReauthRequired'));
  banner.classList.toggle('hidden', !show);
}

// Swap the centre content between the chart view and the ride list when the
// active tab changes. Masthead, tabs, and footer stay put.
function handleTabChange(key) {
  const ridesView = key === 'rides';
  document.getElementById('list-sec')?.classList.toggle('hidden', !ridesView);
  document.querySelector('.chart-section')?.classList.toggle('hidden', ridesView);
  document.querySelector('.stats')?.classList.toggle('hidden', ridesView);
  if (ridesView) showRides();
}

async function getActiveSyncTimestamp() {
  const activeId = await getMeta('activeAdapter');
  const adapter = activeId && syncAdapters.find(a => a.id === activeId);
  if (adapter?.kind !== 'sync') return null;   // footer "Last sync" is sync-only
  const lastOp = await getMeta(`lastOp_${activeId}`);
  return lastOp?.timestamp || null;
}

// Current version of the adapter persistence model (ADR-0005: single
// `activeAdapter`). Bump it when stored adapter state needs another migration;
// migrateAdapterModel runs for anything older.
const ADAPTER_MODEL_VERSION = 2;

// One-time migration to the ADR-0005 single-`activeAdapter` model. The old
// `activeAdapter` held only the last-viewed Settings tab; derive the real active
// source from the legacy keys (still present in returning users' IndexedDB) and
// overwrite it — null clears stale tab-state. `demoActive` and
// `activeSyncAdapter` are no longer written but are read here to migrate older
// installs. When neither is set, nothing owned ongoing ingest, and null is the
// correct result: the rides are historical data with no active source.
async function migrateAdapterModel() {
  if (((await getMeta('adapterModelVersion')) ?? 0) >= ADAPTER_MODEL_VERSION) return;
  const legacy = (await getMeta('activeSyncAdapter'))
    ?? ((await getMeta('demoActive')) ? 'demo-data' : null);
  await setMeta('activeAdapter', legacy);
  await setMeta('adapterModelVersion', ADAPTER_MODEL_VERSION);
}

async function handleDataChange() {
  await refreshCharts();
  closeWelcome();
}

async function _triggerAutosync(adapter) {
  try {
    const latestDate = await getLatestRideDate();
    const result = await adapter.sync(
      msg => console.info(`${adapter.id} sync:`, msg),
      latestDate
    );
    const { savedCount } = await ingestLoad(adapter, result, { trigger: 'autosync' });
    if (savedCount > 0) await refreshCharts();
  } catch (e) {
    console.warn(`${adapter.id} sync failed:`, e.message);
    reportError(e, { adapter: adapter.id, op: 'autosync' });
    if (e.reauthRequired) await updateReauthBanner();
  }
}

function bindCogButton() {
  document.querySelector('.cog')?.addEventListener('click', () => openSettings());
}

function bindReauthBanner() {
  document.getElementById('reauth-banner-cta')
    ?.addEventListener('click', () => openSettings('wahoo'));
  document.getElementById('reauth-banner-dismiss')?.addEventListener('click', () => {
    sessionStorage.setItem('reauthBannerDismissed', '1');
    document.getElementById('reauth-banner')?.classList.add('hidden');
  });
}

function bindDemoBanner() {
  document.getElementById('demo-banner-cta')
    ?.addEventListener('click', () => openSettings());
  document.getElementById('demo-banner-dismiss')?.addEventListener('click', () => {
    sessionStorage.setItem('demoBannerDismissed', '1');
    document.getElementById('demo-banner')?.classList.add('hidden');
  });
}

function bindWelcomeDialog() {
  document.getElementById('btn-welcome-start')?.addEventListener('click', () => {
    closeWelcome();
    openSettings();
  });
}

function openWelcome() {
  const dlg = document.getElementById('welcome');
  if (dlg && !dlg.open) dlg.showModal();
}

function closeWelcome() {
  const dlg = document.getElementById('welcome');
  if (dlg?.open) dlg.close();
}

// ── UI updaters ───────────────────────────────────────────────────────────────

let _lastFullDataset = null;

async function updateTagline(fullDataset) {
  _lastFullDataset = fullDataset;
  const el = document.querySelector('.tagline');
  if (!el) return;
  const userName = await getUserName();
  el.replaceChildren();
  if (!fullDataset || fullDataset.length === 0) {
    el.append('Cycling log');
  } else {
    const ids = fullDataset.map(y => parseInt(y.id)).sort((a, b) => a - b);
    const first = ids[0];
    const last = ids[ids.length - 1];
    const range = document.createElement('b');
    range.textContent = `${first} → ${last}`;
    el.append('Cycling log · ', range);
  }
  if (userName) el.append(` · ${userName}`);
}

function refreshMasthead() {
  return updateTagline(_lastFullDataset);
}

function updateFooterSync(iso) {
  const el = document.getElementById('foot-sync');
  if (!el) return;
  const date = fmtShortDate(iso);
  const time = fmtShortTime(iso);
  el.textContent = date && time ? `Last sync · ${date} · ${time}` : 'Last sync · never';
}

function updateStats(active, full) {
  const distEl = document.getElementById('stat-total-distance');
  const ridesEl = document.getElementById('stat-total-rides');
  const speedEl = document.getElementById('stat-avg-speed');
  const distFoot = document.getElementById('stat-total-distance-foot');
  const ridesFoot = document.getElementById('stat-total-rides-foot');
  const speedFoot = document.getElementById('stat-avg-speed-foot');

  const s = computeStats(active, full);

  if (!active || active.length === 0) {
    if (distEl) distEl.innerHTML = `—<span class="unit">KM</span>`;
    if (ridesEl) ridesEl.textContent = '—';
    if (speedEl) speedEl.innerHTML = `—<span class="unit">KM/H</span>`;
    if (distFoot) distFoot.textContent = 'No years selected';
    if (ridesFoot) ridesFoot.textContent = '';
    if (speedFoot) speedFoot.textContent = '';
    return;
  }

  if (distEl) distEl.innerHTML = `${fmtDistance(s.totalDistance)}<span class="unit">KM</span>`;
  if (ridesEl) ridesEl.textContent = fmtInt(s.totalRides);
  if (speedEl) speedEl.innerHTML = `${fmtSpeed(s.avgSpeed)}<span class="unit">KM/H</span>`;

  if (distFoot) {
    distFoot.innerHTML = s.distDeltaText
      ? `Since ${s.firstYear} · <b>${s.distDeltaText}</b>`
      : `Since ${s.firstYear}`;
  }
  if (ridesFoot) {
    ridesFoot.innerHTML = s.ridesDeltaText
      ? `Avg ${fmtDecimal1(s.avgRidesPerYear)} / yr · <b>${s.ridesDeltaText}</b>`
      : `Avg ${fmtDecimal1(s.avgRidesPerYear)} / yr`;
  }
  if (speedFoot) speedFoot.textContent = s.yearCount === 1 ? 'Season mean' : 'Selected seasons mean';
}

function showLoading() {
  document.getElementById('loading-overlay')?.classList.remove('hidden');
}

function hideLoading() {
  document.getElementById('loading-overlay')?.classList.add('hidden');
}
